import { getServicesData } from '@/api/srp'
import { SEARCH_FLIGHT_SERVER_ERROR, SEARCH_NO_FLIGHTS_AVAILABLE } from '@/constants/Srp'
import { RootState } from '@/redux/store/configureStore'
import { InterstitialData, Fare, SearchData, SearchFareRefs, ServicesData, SrpFareObject, SubTravelOptions, FilterData } from '@/types/srp'
import { deepClone, isEmpty } from '@/utils/general/browserHelper'
import { getServicesDataCacheKey } from '@/utils/srp/helper'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { stat } from 'fs'

interface InitialState {
    searchData: SearchData
    isLoading: boolean
    srpError: string
    responseStatusCode: string
    isServicesLoading: boolean
    servicesData: ServicesData
    commonServicesData: SearchData
    couponData: Record<string, any>
    baggageData: Record<string, any>
    /** WOW pricing — keyed by `fareId`. Populated by `getWowPricingSrpData`. */
    wowFares: Record<string, any>
    /** WOW sort ordering, keyed by journey (e.g. `J1`). Array of `cardId`s. */
    wowSorting: Record<string, string[]>
    /** True while the dynamic-content (WOW) request is in flight. */
    isDynamicContentLoading: boolean
    /** Card indices currently being fetched (progressive loading batches). */
    progressiveLoadingIndices: number[]
    /** travelOptionIds returned from the high-RPS endpoint, used by WOW UI. */
    highRpsTravelOptionIds: string[]
    /**
     * fareIds (or combined `[onwardFareId, returnFareId]` keys) whose WOW data
     * is currently being fetched by an ad-hoc/explicit call (e.g. same-price
     * expansion, RT both-legs selection). Used by `WowFareCallout` to render
     * a shimmer for those specific cards while the fetch is in flight.
     */
    wowLoadingFareIds: string[]
    selectedFilters: FilterData
    /**
     * travelOptionIds whose interstitial (fetch-fare) call has already completed this
     * session. Lets the one-way FareFamilyModal reuse the merged fares on reopen instead
     * of firing a fresh interstitial call. Reset whenever a new search response lands.
     */
    interstitialFetchedKeys: Record<string, boolean>
    /**
     * Per-`subTravelOptionId` fare pointer captured from the search response, keyed the
     * same way as `searchData.subTravelOptions`. `mergeInterstitialData` replaces those
     * subTravelOption entries wholesale with whatever the last interstitial response
     * carried — and a special-RT response describes the trip through its combined
     * fare-combo map, not through per-leg fares — so the per-leg fare a subsequent
     * /flight/fetch-interstitial-page request has to echo back is read from here
     * instead. Rebuilt on every search, cleared on flush.
     */
    searchFareRefs: SearchFareRefs
    /**
     * The search response's own `subTravelOptions`, untouched by any interstitial
     * response. `mergeInterstitialData` replaces `fareIds` (and `cheapestFareId` /
     * `sortList`) with the full fare family the interstitial returns, which is
     * ordered cheapest-first — so once the fare-family modal had been opened, the
     * SRP tuple re-rendered from that and lost the search response's lead fare
     * (the branded one). The tuple strip renders from here instead, so opening and
     * closing the modal can no longer reorder it. Rebuilt on every search, cleared
     * on flush. Same rationale as `searchFareRefs` above.
     */
    searchSubTravelOptions: SubTravelOptions
}
const defaultState = {
    cards: {
        J1: []
    },
    sortData: {},
    filterData: {},
    metaData: {
        airlineDetail: {},
        airportDetail: {}
    },
    searchIntent: {},
    subTravelOptions: {},
    fares: {},
    dataId: '',
    travelOptionIdsToFareIdsMap: {},
    searchId: ''
}
const initialState: InitialState = {
    searchData: {
        cards: {
            J1: []
        },
        sortData: {},
        filterData: {},
        metaData: {
            airlineDetail: {},
            airportDetail: {}
        },
        searchIntent: {},
        subTravelOptions: {},
        fares: {},
        dataId: '',
        travelOptionIdsToFareIdsMap: {},
        searchId: ''
    },
    isLoading: true,
    srpError: '',
    responseStatusCode: '',
    isServicesLoading: true,
    servicesData: {
        benefits: {},
        baggageAllowances: {},
        cardIdToBenefitsMap: {}
    },
    commonServicesData: {},
    couponData: {},
    baggageData: {},
    wowFares: {},
    wowSorting: {},
    isDynamicContentLoading: true,
    progressiveLoadingIndices: [],
    highRpsTravelOptionIds: [],
    wowLoadingFareIds: [],
    selectedFilters: {},
    interstitialFetchedKeys: {},
    searchFareRefs: {},
    searchSubTravelOptions: {}
}

export const fetchServicesData = createAsyncThunk('srpStore/services', async (payload: object) => {
    try {
        const response = await getServicesData(payload)
        return response
    } catch (e) {
        return e
    }
})

const mergedCardIdToBenefitsMap = (srpCardIdToBenefitsMap: any, stateCardIdToBenefitsMap: any) => {
    Object.keys(srpCardIdToBenefitsMap).forEach(key => {
        if (stateCardIdToBenefitsMap?.[key]) {
            const { subTravelOptions = [] } = srpCardIdToBenefitsMap[key] || {}
            subTravelOptions.forEach((subTravelOption: any) => {
                const { subTravelOptionId = '', benefits = {} } = subTravelOption || {}
                const existingSubTravelOption = stateCardIdToBenefitsMap[key]?.subTravelOptions?.find(
                    (subTravelOption: any) => subTravelOption.subTravelOptionId === subTravelOptionId
                )
                if (!isEmpty(existingSubTravelOption)) {
                    Object.keys(benefits).forEach(key => {
                        const {
                            penalties: penalty = [],
                            benefits: benefitIds = [],
                            baggageAllowanceId = ''
                        } = benefits[key] || {}
                        if (isEmpty(existingSubTravelOption.benefits?.[key])) {
                            existingSubTravelOption.benefits = {
                                ...existingSubTravelOption.benefits,
                                [key]: { penalty, benefitIds, baggageAllowanceId }
                            }
                        } else {
                            // TODO, remove duplicates
                            existingSubTravelOption.benefits[key] = {
                                penalty: [...(existingSubTravelOption.benefits[key]?.penalty || []), ...penalty],
                                benefitIds: [
                                    ...(existingSubTravelOption.benefits[key]?.benefitIds || []),
                                    ...benefitIds
                                ],
                                baggageAllowanceId
                            }
                        }
                    })
                } else {
                    stateCardIdToBenefitsMap[key].subTravelOptions.push(subTravelOption)
                }
            })
        } else {
            stateCardIdToBenefitsMap[key] = srpCardIdToBenefitsMap[key]
        }
    })
    return stateCardIdToBenefitsMap
}

const buildSearchFareRefs = (response: any): SearchFareRefs => {
    const { subTravelOptions = {}, fares = {} } = response || {}
    return Object.keys(subTravelOptions).reduce((refs: SearchFareRefs, subTravelOptionId: string) => {
        const { cheapestFareId = '', fareIds = [] } = subTravelOptions[subTravelOptionId] || {}
        const fareId = cheapestFareId || fareIds?.[0] || ''
        if (fareId) {
            refs[subTravelOptionId] = {
                fareId,
                price: fares?.[fareId]?.pricing?.totalPricing?.totalPrice ?? ''
            }
        }
        return refs
    }, {})
}

/** Fields that must not be blanked by a response that simply omits them. */
const PRESERVED_SUB_TRAVEL_OPTION_FIELDS = ['fareIds', 'fareList', 'sortList', 'cheapestFareId', 'totalFareCount', 'cabinClassSummary'] as const

/**
 * Merges incoming subTravelOptions into existing ones field by field.
 *
 * An incoming entry wins for every field it actually carries. Fields it omits,
 * or sends empty when we already hold real data, are kept — a special-RT
 * response describes the trip through its combined fare-combo map and ships no
 * per-leg fare family, and a plain spread let that wipe one a regular-RT
 * response had already provided for the same leg.
 */
const mergeSubTravelOptions = (existing: any = {}, incoming: any = {}): any => {
    const merged: any = { ...existing }
    for (const [id, incomingEntry] of Object.entries<any>(incoming || {})) {
        const current = existing?.[id]
        if (!current) {
            merged[id] = incomingEntry
            continue
        }
        const next = { ...current, ...incomingEntry }
        for (const field of PRESERVED_SUB_TRAVEL_OPTION_FIELDS) {
            const incomingValue = (incomingEntry as any)?.[field]
            const isMissing =
                incomingValue === undefined ||
                incomingValue === null ||
                (Array.isArray(incomingValue) && incomingValue.length === 0)
            if (isMissing && (current as any)?.[field] !== undefined) {
                next[field] = (current as any)[field]
            }
        }
        merged[id] = next
    }
    return merged
}

export const srpManager = createSlice({
    name: 'srpSeachData',
    initialState,
    reducers: {
        setError(state, action) {
            const error = action.payload
            state.isLoading = false
            state.srpError = error
        },
        setSelectedFilters(state, action: PayloadAction<FilterData>) {
            state.selectedFilters = action.payload
        },
        setCardIdToBenefitsMap(state, action) {
            const srpCardIdToBenefitsMap = action.payload
            const stateCardIdToBenefitsMap = deepClone({ ...state.servicesData.cardIdToBenefitsMap })
            const mergedCardIds = mergedCardIdToBenefitsMap(srpCardIdToBenefitsMap, stateCardIdToBenefitsMap)
            state.servicesData.cardIdToBenefitsMap = mergedCardIds
        },
        setCouponsData(state, action) {
            state.couponData = {
                ...state.couponData,
                ...action.payload
            }
        },
        markInterstitialFetched(state, action: PayloadAction<string>) {
            if (action.payload) {
                state.interstitialFetchedKeys[action.payload] = true
            }
        },
        setBaggageData(state, action) {
            state.baggageData = {
                ...state.baggageData,
                ...action.payload
            }
        },
        setSrpSearchData(state, action) {
            const { response } = action.payload
            const { cards, statusCode } = response || {}
            // A fresh search invalidates any previously reusable interstitial payloads.
            state.interstitialFetchedKeys = {}
            state.searchFareRefs = {}
            state.searchSubTravelOptions = {}
            if (!isEmpty(cards)) {
                const { benefits, baggageAllowances } = response || {}
                if (!isEmpty(cards?.J1)) {
                    state.searchData = response || {}
                    state.searchFareRefs = buildSearchFareRefs(response)
                    // Copied, not aliased: `mergeInterstitialData` rebuilds
                    // `searchData.subTravelOptions`, and this must not follow it.
                    state.searchSubTravelOptions = { ...(response?.subTravelOptions || {}) }
                    state.responseStatusCode = String(statusCode || 200)
                    state.servicesData = {
                        cardIdToBenefitsMap: {},
                        benefits: {
                            ...benefits
                        },
                        baggageAllowances: {
                            ...baggageAllowances
                        }
                    }
                } else {
                    state.searchData = defaultState
                    state.srpError = SEARCH_NO_FLIGHTS_AVAILABLE
                    state.responseStatusCode = String(statusCode || 'N/A')
                }
            } else {
                state.searchData = defaultState
                if (statusCode === 404) {
                    state.srpError = SEARCH_NO_FLIGHTS_AVAILABLE
                    state.responseStatusCode = String(statusCode)
                } else {
                    state.srpError = SEARCH_FLIGHT_SERVER_ERROR
                    state.responseStatusCode = String(statusCode || 'N/A')
                }
            }
            state.isLoading = false
        },
        mergeInterstitialData(state, action: PayloadAction<InterstitialData>) {
            state.searchData = {
                ...state.searchData,
                fares: {
                    ...state.searchData.fares,
                    ...action.payload.fares
                },
                /**
                 * Merged field-wise, not entry-wise.
                 *
                 * A plain spread replaces each subTravelOption wholesale, so a
                 * response that describes an entry only partially — a special-RT
                 * payload carries the trip as one combined fare and sends no
                 * per-leg `fareIds`/`fareList` — erased the fare family a previous
                 * regular-RT response had put there. Everything downstream then
                 * resolved that leg to no fares at all. Incoming fields win;
                 * absent ones leave what is already there alone.
                 */
                subTravelOptions: mergeSubTravelOptions(
                    state.searchData.subTravelOptions,
                    action.payload.subTravelOptions
                ),
                baggageAllowances: {
                    ...state.searchData.baggageAllowances,
                    ...action.payload.baggageAllowances
                },
                benefits: {
                    ...state.searchData.benefits,
                    ...action.payload.benefits  
                }
            }
            state.servicesData = {
                ...state.servicesData,
                baggageAllowances: {
                    ...state.servicesData.baggageAllowances,
                    ...action.payload.baggageAllowances
                },
                benefits: {
                    ...state.servicesData.benefits,
                    ...action.payload.benefits  
                }
            }
            state.commonServicesData = {
                ...state.commonServicesData,
                ...action.payload,
                benefits: {...state?.commonServicesData?.benefits, ...action.payload?.benefits},
                baggageAllowances: {...state?.commonServicesData?.baggageAllowances, ...action.payload?.baggageAllowances},
                travelOptions: {...state?.commonServicesData?.travelOptions, ...action.payload?.travelOptions},
                fareBenefits: {...state?.commonServicesData?.fareBenefits, ...action.payload?.fareBenefits},
                travelOptionIdsToFareIdsMap: {...state?.commonServicesData?.travelOptionIdsToFareIdsMap, ...action.payload?.travelOptionIdsToFareIdsMap}
            }
        },
        setServicesLoading(state, action) {
            state.isServicesLoading = action.payload
        },
        flushData(state) {
            state.searchData = defaultState
            state.searchFareRefs = {}
            state.searchSubTravelOptions = {}
            // Reuse flags describe data this reset is about to discard; leaving
            // them behind would let a later modal open claim fares that are gone.
            state.interstitialFetchedKeys = {}
            state.isLoading = true
            state.srpError = ''
            state.responseStatusCode = ''
            state.isServicesLoading = true
            state.couponData = {}
            state.servicesData = {
                benefits: {},
                baggageAllowances: {},
                cardIdToBenefitsMap: {}
            }
            state.commonServicesData = {}
            state.wowFares = {}
            state.wowSorting = {}
            state.isDynamicContentLoading = true
            state.progressiveLoadingIndices = []
            state.highRpsTravelOptionIds = []
            state.wowLoadingFareIds = []
        },
        /**
         * WOW pricing — replace fares pricing wholesale (typical first-load
         * response). Mirrors flights-pwa srpManager.updateFaresPricing.
         */
        updateFaresPricing(
            state,
            action: PayloadAction<{
                faresPricingData?: Record<string, any>
                wowSorting?: Record<string, string[]>
                highRpsTravelOptionIds?: string[]
            }>
        ) {
            const { faresPricingData = {}, wowSorting = {}, highRpsTravelOptionIds = [] } =
                action.payload || {}
            state.wowFares = {
                ...state.wowFares,
                ...faresPricingData
            }
            state.wowSorting = {
                ...state.wowSorting,
                ...wowSorting
            }
            state.highRpsTravelOptionIds = highRpsTravelOptionIds
            state.isDynamicContentLoading = false
        },
        /**
         * WOW pricing — incremental merge for progressive batches.
         */
        mergeWowFares(state, action: PayloadAction<Record<string, any>>) {
            state.wowFares = {
                ...state.wowFares,
                ...(action.payload || {})
            }
        },
        setDynamicContentLoading(state, action: PayloadAction<boolean>) {
            state.isDynamicContentLoading = !!action.payload
        },
        setProgressiveLoadingIndices(state, action: PayloadAction<number[]>) {
            state.progressiveLoadingIndices = action.payload || []
        },
        /** Add card indices to the progressive-loading set (idempotent). */
        addProgressiveLoadingIndices(state, action: PayloadAction<number[]>) {
            const toAdd = action.payload || []
            const existing = new Set(state.progressiveLoadingIndices)
            toAdd.forEach(i => existing.add(i))
            state.progressiveLoadingIndices = Array.from(existing)
        },
        /** Remove card indices from the progressive-loading set. */
        removeProgressiveLoadingIndices(state, action: PayloadAction<number[]>) {
            const toRemove = new Set(action.payload || [])
            state.progressiveLoadingIndices = state.progressiveLoadingIndices.filter(
                i => !toRemove.has(i)
            )
        },
        /** Mark fareIds as having a WOW fetch in flight (renders shimmer). */
        addWowLoadingFareIds(state, action: PayloadAction<string[]>) {
            const toAdd = (action.payload || []).filter(Boolean)
            const existing = new Set(state.wowLoadingFareIds)
            toAdd.forEach(id => existing.add(id))
            state.wowLoadingFareIds = Array.from(existing)
        },
        /** Clear in-flight markers for the given fareIds. */
        removeWowLoadingFareIds(state, action: PayloadAction<string[]>) {
            const toRemove = new Set((action.payload || []).filter(Boolean))
            state.wowLoadingFareIds = state.wowLoadingFareIds.filter(id => !toRemove.has(id))
        }
    },
    extraReducers(builder) {
        builder.addCase(fetchServicesData.pending, (state, action) => {
            state.isServicesLoading = true
        })
        builder.addCase(fetchServicesData.fulfilled, (state, action) => {
            const data = action?.payload || {}

            const { cardIdToBenefitsMap, benefits, baggageAllowances } = getServicesDataCacheKey(data) || {}
            state.servicesData = {
                ...state.servicesData,
                benefits: {
                    ...state.servicesData.benefits,
                    ...benefits
                },
                baggageAllowances: {
                    ...state.servicesData.baggageAllowances,
                    ...baggageAllowances
                },
                cardIdToBenefitsMap: mergedCardIdToBenefitsMap(
                    cardIdToBenefitsMap,
                    deepClone({ ...state.servicesData.cardIdToBenefitsMap })
                )
            }
            state.commonServicesData = {
                ...state.commonServicesData,
                benefits: {...state?.commonServicesData?.benefits, ...data?.benefits},
                baggageAllowances: {...state?.commonServicesData?.baggageAllowances, ...data?.baggageAllowances},
                travelOptions: {...state?.commonServicesData?.travelOptions, ...data?.travelOptions},
                fareBenefits: {...state?.commonServicesData?.fareBenefits, ...data?.fareBenefits}
            }
            state.isServicesLoading = false
        })
        builder.addCase(fetchServicesData.rejected, (state, action) => {
            state.isServicesLoading = false
        })
    }
})

const { reducer, actions } = srpManager

const {
    setError,
    setSrpSearchData,
    mergeInterstitialData,
    flushData,
    setCouponsData,
    setBaggageData,
    setCardIdToBenefitsMap,
    setServicesLoading,
    updateFaresPricing,
    mergeWowFares,
    setDynamicContentLoading,
    setProgressiveLoadingIndices,
    addProgressiveLoadingIndices,
    removeProgressiveLoadingIndices,
    addWowLoadingFareIds,
    removeWowLoadingFareIds,
    setSelectedFilters,
    markInterstitialFetched
} = actions

export const getSrpSearchData = (state: RootState) => state.srpSeachData
export const getSelectedFilters = (state: RootState) => state.srpSeachData.selectedFilters
/** Search-response subTravelOptions, never rewritten by an interstitial merge. */
export const getSearchSubTravelOptions = (state: RootState) => state.srpSeachData.searchSubTravelOptions

/** WOW pricing selectors — keep callsites tidy. */
export const getWowFares = (state: RootState) => state.srpSeachData.wowFares
export const getWowSorting = (state: RootState) => state.srpSeachData.wowSorting
export const getIsDynamicContentLoading = (state: RootState) =>
    state.srpSeachData.isDynamicContentLoading
export const getProgressiveLoadingIndices = (state: RootState) =>
    state.srpSeachData.progressiveLoadingIndices
export const getHighRpsTravelOptionIds = (state: RootState) =>
    state.srpSeachData.highRpsTravelOptionIds
export const getWowLoadingFareIds = (state: RootState) =>
    state.srpSeachData.wowLoadingFareIds

export {
    setError,
    setSrpSearchData,
    mergeInterstitialData,
    flushData,
    setCouponsData,
    setBaggageData,
    setCardIdToBenefitsMap,
    setServicesLoading,
    updateFaresPricing,
    mergeWowFares,
    setDynamicContentLoading,
    setProgressiveLoadingIndices,
    addProgressiveLoadingIndices,
    removeProgressiveLoadingIndices,
    addWowLoadingFareIds,
    removeWowLoadingFareIds,
    setSelectedFilters,
    markInterstitialFetched
}

export default reducer
